import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AddIcon from '@mui/icons-material/Add'
import Application from '../../../layout/Application'
import AlertCardList from '../components/AlertCardList'
import StandardAlertTemplatesTable from '../components/StandardAlertTemplatesTable'
import { AlertProvider, useAlertContext } from '../contexts/AlertContext'
import AlertFilterView from '../components/AlertFilterView'
import { Tabs } from '../../../layout/MainLayout'
import CreateAlertActionButton from '../../../layout/settingsMenu/CreateAlertActionButton'
import { useTheme } from '@mui/material/styles'
import { Alert, useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { getMemberPortalUrl } from '../../../common/utils/memberPortalUrl'
import { useEffect } from 'react'

function AlertAppContent() {
  const { activeTab, setActiveTab, autoTraderUsage, tradingHouse, usermeta } =
    useApplicationSuiteContext()

  const {
    openSidePanel,
    viewMode,
    setViewMode,
    closeSidePanel,
    closeTemplateSidePanel,
    openTemplateSidePanel,
  } = useAlertContext()

  useEffect(() => {
    const alert = activeTab.options?.item as Alert
    if (activeTab.tab === Tabs.Alerts && alert) {
      openSidePanel(alert)
      delete activeTab.options!.item
      setActiveTab({ tab: Tabs.Alerts, options: activeTab.options })
    }
  }, [openSidePanel, setActiveTab, activeTab])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const fullAutoTraderPools = [
    {
      label: 'Basic AutoTraders',
      note: 'Basic AutoTraders run DTU standard alerts. ',
      ...autoTraderUsage.dtu,
    },
    { label: 'AutoTraders', note: '', ...autoTraderUsage.any },
  ].filter((pool) => pool.limit > 0 && pool.used >= pool.limit)

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        flexDirection: 'column',
      }}
      px={1}
    >
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          boxShadow: 'none',
          background: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          mb: 1,
        }}
      >
        <Toolbar style={{ padding: 0, display: 'flex', gap: 8, minHeight: 56 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={1}
            alignItems={isMobile ? '' : 'center'}
            sx={{ flexGrow: 1 }}
            mb={isMobile ? '8px' : 0}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <NotificationsActiveIcon sx={{ color: theme.palette.primary.main }} />
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                Alerts
              </Typography>
            </Box>
            <Box display={'flex'} width={'100%'} gap={1} alignItems="center">
              <AlertFilterView />
              {usermeta?.isAdmin && (
                <ToggleButtonGroup
                  color="primary"
                  value={viewMode}
                  exclusive
                  size="small"
                  onChange={(_, newMode) => {
                    if (!newMode) return
                    setViewMode(newMode)
                    closeSidePanel()
                    closeTemplateSidePanel()
                  }}
                >
                  <ToggleButton value="alerts">Your Alerts</ToggleButton>
                  {/* Admin treatment on this tab only: the group also holds the ordinary
                      alerts view, which shouldn't read as an admin surface. */}
                  <ToggleButton
                    value="templates"
                    sx={{
                      gap: 0.5,
                      '&.Mui-selected': {
                        color: 'warning.main',
                        '&:hover': { color: 'warning.main' },
                      },
                    }}
                  >
                    <AdminPanelSettings fontSize="small" />
                    Starter Plus Templates
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <Box ml={'auto'} display="flex" alignItems="center" gap={1}>
                {fullAutoTraderPools.map((pool) => (
                  <Tooltip
                    key={pool.label}
                    title={
                      <span>
                        {pool.used} of {pool.limit} {pool.label} active. {pool.note}
                        <a
                          href={getMemberPortalUrl(
                            tradingHouse,
                            '/dashboard/marketplace?display=autotrader'
                          )}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'inherit' }}
                        >
                          Upgrade for more.
                        </a>
                      </span>
                    }
                    arrow
                  >
                    <Chip
                      label={`${pool.used}/${pool.limit} ${pool.label}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ cursor: 'default', fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                ))}
                {viewMode === 'templates' ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openTemplateSidePanel(null)}
                    sx={{ flexShrink: 0 }}
                  >
                    New Template
                  </Button>
                ) : (
                  <CreateAlertActionButton asButton={true} color="primary" />
                )}
              </Box>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      {viewMode === 'templates' ? <StandardAlertTemplatesTable /> : <AlertCardList />}
    </Box>
  )
}

const AlertProviderWrapper = ({ children }: any) => {
  return (
    <AlertProvider parentTab={Tabs.Alerts} offerTemplates>
      {children}
    </AlertProvider>
  )
}

const App = ({ tab }: any) => (
  <Application AppComponent={AlertAppContent} ContextProviders={[AlertProviderWrapper]} tab={tab} />
)
export default App
